/**
 * useScrollCanvas
 * Scroll-driven 239-frame canvas animation for the cinematic hero.
 *
 * BUG HISTORY:
 *
 * v1 bug — "stuck on first frame":
 *   rect.height on a sticky container returns visual height (1×vh) not layout
 *   height (10×vh), so scrollable was always ~0 and rawProgress always 0.
 *   Fix: use container.offsetHeight.
 *
 * v2 bug — "works briefly then stops":
 *   useEffect dependency array included useCallback functions. Every setState
 *   call (loadProgress, scrollProgress) triggered a re-render → effect cleanup
 *   → cache.fill(null) wiped all frames → scroll listener dropped then re-added.
 *   Fix: all functions as plain refs, useEffect([]) runs exactly once.
 *
 * v3 bug — "animation loops/resets mid-scroll + freezes after pause" (this fix):
 *   Two issues seen in Samsung S24 Chrome video:
 *
 *   LOOP/RESET: containerTop was recalculated on every scroll event via
 *   getBoundingClientRect().top + scrollY. On Samsung Chrome the address bar
 *   collapses mid-scroll, which physically shifts getBoundingClientRect().top.
 *   This made containerTop a moving target — scroll progress snapped backward
 *   and the animation replayed from near the beginning mid-scroll.
 *   Fix: cache containerTop once on mount and update only on true resize events.
 *   During scroll, use the cached value exclusively.
 *
 *   FREEZE AFTER PAUSE: lastDrawnFrame guard blocked repaints when the user
 *   paused mid-scroll. When they resumed, the guard saw frameIdx === lastDrawnFrame
 *   and skipped painting even though the canvas was correct — but a browser
 *   compositor flush between the pause and resume could clear the canvas,
 *   leaving a blank frame. Direct scroll-driven painting is cheap enough to
 *   always repaint. Guard removed.
 */

import { useRef, useEffect, useState } from 'react';

// ─── Constants ────────────────────────────────────────────────────────────────

const FRAME_FIRST       = 2;
const FRAME_LAST        = 240;
const FRAME_COUNT       = FRAME_LAST - FRAME_FIRST + 1; // 239
const FRAME_BASE_PATH   = '/frames/';
const FRAME_PREFIX      = 'ezgif-frame-';
const FRAME_EXT         = '.jpg';
const SCROLL_MULTIPLIER = 10;
const PRIORITY_BATCH    = 60;
const IDLE_BATCH        = 8;
const LOADER_HIDE_DELAY_MS = 950;

// ─── Pure helpers ─────────────────────────────────────────────────────────────

function pad3(n) {
  return String(n).padStart(3, '0');
}

function buildFrameUrls() {
  const urls = [];
  for (let i = FRAME_FIRST; i <= FRAME_LAST; i++) {
    urls.push(`${FRAME_BASE_PATH}${FRAME_PREFIX}${pad3(i)}${FRAME_EXT}`);
  }
  return urls;
}

function scrollToFrameIndex(progress) {
  const t = progress * progress * (3 - 2 * progress); // smoothstep
  return Math.min(Math.floor(t * (FRAME_COUNT - 1)), FRAME_COUNT - 1);
}

function drawCover(ctx, img, canvasW, canvasH) {
  if (!img || !img.complete || img.naturalWidth === 0) return;
  const scale   = Math.max(canvasW / img.naturalWidth, canvasH / img.naturalHeight);
  const drawW   = img.naturalWidth  * scale;
  const drawH   = img.naturalHeight * scale;
  const offsetX = (canvasW - drawW) / 2;
  const offsetY = (canvasH - drawH) / 2;
  ctx.clearRect(0, 0, canvasW, canvasH);
  ctx.drawImage(img, offsetX, offsetY, drawW, drawH);
}

function getStableViewportHeight() {
  if (typeof window === 'undefined') return 0;
  return window.visualViewport ? window.visualViewport.height : window.innerHeight;
}

function updateVhProperty() {
  document.documentElement.style.setProperty('--vh', `${getStableViewportHeight() * 0.01}px`);
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useScrollCanvas() {
  const canvasRef    = useRef(null);
  const containerRef = useRef(null);

  // Internal mutable state — all refs, never trigger re-renders
  const frameCache      = useRef(new Array(FRAME_COUNT).fill(null));
  const frameUrls       = useRef(buildFrameUrls());
  const loadedCount     = useRef(0);
  const dpr             = useRef(1);
  const canvasDims      = useRef({ w: 0, h: 0 });

  // CRITICAL: containerTop cached and updated ONLY on mount + resize.
  // Never recalculate during scroll — address bar collapse on Samsung Chrome
  // shifts getBoundingClientRect().top mid-scroll, causing progress to reset.
  const containerTopRef = useRef(0);

  // React UI state
  const [loadProgress,   setLoadProgress]   = useState(0);
  const [isReady,        setIsReady]        = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [parallaxOffset, setParallaxOffset] = useState(0);
  const [loaderVisible,  setLoaderVisible]  = useState(true);

  const setState = useRef({ setLoadProgress, setScrollProgress, setParallaxOffset, setIsReady, setLoaderVisible });
  // This effect intentionally runs after every render to keep setState.current fresh.
  // No dependency array = runs every render by design. ESLint warning suppressed.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    setState.current = { setLoadProgress, setScrollProgress, setParallaxOffset, setIsReady, setLoaderVisible };
  });

  // ── paintFrame — no lastDrawnFrame guard ──────────────────────────────────
  // Guard removed: direct scroll painting is cheap, and the guard caused
  // visible freezes after the user paused mid-scroll then resumed.

  const paintFrameRef = useRef(null);
  paintFrameRef.current = (frameIdx) => {
    const cache = frameCache.current;
    let idx = frameIdx;

    if (!cache[idx] || !cache[idx].complete) {
      let lo = idx - 1, hi = idx + 1, found = -1;
      while (lo >= 0 || hi < FRAME_COUNT) {
        if (lo >= 0 && cache[lo]?.complete) { found = lo; break; }
        if (hi < FRAME_COUNT && cache[hi]?.complete) { found = hi; break; }
        lo--; hi++;
      }
      if (found === -1) return;
      idx = found;
    }

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx      = canvas.getContext('2d');
    const { w, h } = canvasDims.current;
    const d        = dpr.current;

    ctx.setTransform(d, 0, 0, d, 0, 0);
    drawCover(ctx, cache[idx], w, h);
  };

  // ── handleScroll — uses CACHED containerTop, never getBoundingClientRect ──

  const handleScrollRef = useRef(null);
  handleScrollRef.current = () => {
    const container = containerRef.current;
    if (!container) return;

    // Use cached containerTop — stable even when address bar collapses.
    // offsetHeight = true layout height (10×vh), safe to read during scroll.
    const containerTop = containerTopRef.current;
    const scrollable   = container.offsetHeight - getStableViewportHeight();

    if (scrollable <= 0) {
      paintFrameRef.current(0);
      return;
    }

    const scrolled    = Math.max(0, window.scrollY - containerTop);
    const rawProgress = Math.min(scrolled / scrollable, 1);
    const frameIdx    = scrollToFrameIndex(rawProgress);

    paintFrameRef.current(frameIdx);

    setState.current.setScrollProgress(rawProgress);
    setState.current.setParallaxOffset(rawProgress * 2 - 1);
  };

  // ── updateContainerTop — called on mount + resize only ────────────────────

  const updateContainerTopRef = useRef(null);
  updateContainerTopRef.current = () => {
    const container = containerRef.current;
    if (!container) return;
    // Use offsetTop which is layout-stable (not affected by address bar).
    // Walk up to get true document offset in case of nested positioned parents.
    let top = 0;
    let el = container;
    while (el) {
      top += el.offsetTop;
      el = el.offsetParent;
    }
    containerTopRef.current = top;
  };

  // ── resizeCanvas — updates containerTop cache too ────────────────────────

  const resizeCanvasRef = useRef(null);
  resizeCanvasRef.current = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const pixelRatio = window.devicePixelRatio || 1;
    dpr.current      = pixelRatio;

    const w = window.innerWidth;
    const h = getStableViewportHeight();

    canvas.style.width  = `${w}px`;
    canvas.style.height = `${h}px`;
    canvas.width        = Math.round(w * pixelRatio);
    canvas.height       = Math.round(h * pixelRatio);

    canvasDims.current = { w, h };

    // Recache containerTop after layout changes from resize
    updateContainerTopRef.current();

    // Repaint at current scroll position
    handleScrollRef.current();
  };

  // ── Frame loading ─────────────────────────────────────────────────────────

  const loadFrameRef = useRef(null);
  loadFrameRef.current = (index) => {
    return new Promise((resolve) => {
      if (frameCache.current[index]?.complete) {
        resolve(frameCache.current[index]);
        return;
      }

      const img = new Image();
      const url = frameUrls.current[index];

      const onDecoded = () => {
        frameCache.current[index] = img;
        loadedCount.current += 1;
        setState.current.setLoadProgress(loadedCount.current / FRAME_COUNT);
        resolve(img);
      };

      img.onload = () => {
        if (typeof img.decode === 'function') {
          img.decode().then(onDecoded).catch(onDecoded);
        } else {
          onDecoded();
        }
      };

      img.onerror = () => {
        loadedCount.current += 1;
        setState.current.setLoadProgress(loadedCount.current / FRAME_COUNT);
        resolve(null);
      };

      img.src = url;
    });
  };

  const loadAllFramesRef = useRef(null);
  loadAllFramesRef.current = async () => {
    const priorityIndices = Array.from({ length: Math.min(PRIORITY_BATCH, FRAME_COUNT) }, (_, i) => i);
    await Promise.all(priorityIndices.map(i => loadFrameRef.current(i)));

    setState.current.setIsReady(true);
    paintFrameRef.current(0);

    setTimeout(() => setState.current.setLoaderVisible(false), LOADER_HIDE_DELAY_MS);

    const remaining = Array.from({ length: FRAME_COUNT - PRIORITY_BATCH }, (_, i) => i + PRIORITY_BATCH);

    function scheduleBatch(startIdx) {
      if (startIdx >= remaining.length) return;
      setTimeout(async () => {
        const slice = remaining.slice(startIdx, startIdx + IDLE_BATCH);
        await Promise.all(slice.map(i => loadFrameRef.current(i)));
        handleScrollRef.current();
        scheduleBatch(startIdx + IDLE_BATCH);
      }, 16);
    }

    scheduleBatch(0);
  };

  // ── Mount: runs exactly ONCE ──────────────────────────────────────────────

  useEffect(() => {
    updateVhProperty();
    resizeCanvasRef.current();

    // containerTop is calculated lazily on first scroll via a rAF after mount.
    // Calculating it eagerly in useEffect is too early — the --vh CSS variable
    // that drives the container height may not have propagated to layout yet,
    // causing offsetTop to return 0 and breaking all scroll math.
    // Two rAF ticks guarantees the browser has done a full layout pass.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        updateContainerTopRef.current();
        handleScrollRef.current(); // repaint at correct position after layout
      });
    });

    const onScroll = () => handleScrollRef.current();

    const onResize = () => {
      updateVhProperty();
      resizeCanvasRef.current(); // internally calls updateContainerTop + handleScroll
    };

    window.addEventListener('scroll', onScroll, { passive: true });

    const ro = new ResizeObserver(onResize);
    if (containerRef.current) ro.observe(containerRef.current);

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', onResize);
    } else {
      window.addEventListener('resize', onResize);
    }

    // Paint frame 0 immediately while frames load
    requestAnimationFrame(() => paintFrameRef.current(0));

    loadAllFramesRef.current();

    return () => {
      window.removeEventListener('scroll', onScroll);
      ro.disconnect();
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', onResize);
      } else {
        window.removeEventListener('resize', onResize);
      }
    };
  }, []);

  return {
    canvasRef,
    containerRef,
    loadProgress,
    isReady,
    loaderVisible,
    scrollProgress,
    parallaxOffset,
    scrollMultiplier: SCROLL_MULTIPLIER,
  };
}
